import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { RecipesDataService, RecipesColumnsService } from '../../services';
import { DialogRecipeFormComponent } from './recipe-form/recipe-form.component';
import { DialogRecipeDetailsComponent } from './recipe-details/recipe-details.component';
import { Recipe, RecipesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-recipes',
  templateUrl: './recipes.component.html',
  styleUrls: ['./recipes.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class RecipesComponent implements OnInit, OnDestroy {

  private recipeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private recipeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private recipeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public recipeToDelete: Recipe = {};
  public recipes: Recipe[] = [];
  public recipe: Recipe = {};
  public selectedRecipes: Recipe[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private recipesDataService: RecipesDataService,
    private recipesColumnsService: RecipesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('RecipesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Recipes - Management');
    this.primengConfig.ripple = true;
    this.getRecipes();
  }

  ngOnDestroy(): void {
    console.log('RecipesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getRecipes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.recipesDataService.getRecipes(this.parameters).subscribe((response: RecipesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.recipes = response.results;
        } else {
          this.recipes = this.recipes.concat(response.results);
        }
        if (response.inlineCount > this.recipes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getRecipes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeSNOTLOADED') });
    });
  }

  importRecipes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.recipesDataService.addRecipes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('RecipeSCREATED') });
                if (proceed === j) {
                  this.getRecipes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeSNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeSNOTCREATED') + ' ' + msg });
            });
          } else {
            this.recipesDataService.updateRecipes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('RecipeSUPDATED') });
                if (proceed === j) {
                  this.getRecipes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeSNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeSNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.recipesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('recipes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedRecipes() {
    this.confirmationService.confirm({
      // message: 'Are you sure you want to delete the selected Recipes?',
      message: this.translate.instant('AREYOUSURETODELETEDRecipe'),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.recipesDataService.deleteRecipes(this.selectedRecipes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: this.translate.instant('RecipeDELETED'), life: 3000 });
            this.getRecipes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('RecipeNOTDELETED') });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('RecipeNOTDELETED') });
        });
        this.selectedRecipes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.recipes, this.columns, 'Recipes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.recipes, this.columns, 'Recipes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.recipes, this.columns, 'Recipes');
  }

  refresh() {
    this.recipesDataService.clearCache();
    this.getRecipes();
  }

  recipeAdd(): void {
    if (!this.navigation.getParamValue('recipes.addInDialog', true)) {
      this.router.navigate(['/recipes/add']);
      return;
    }
    this.recipeAddDialog = this.dialogService.open(DialogRecipeFormComponent, {
      header: '', // this.translate.instant('NEWrecipe'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        recipe: {id: null},
        users: this.recipesColumnsService.users,
      },
    });

    this.recipeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: this.translate.instant('recipeCREATED') });
        this.getRecipes();
      }
    });
  }

  recipeEdit(recipe: Recipe): void {
    if (!this.navigation.getParamValue('recipes.editInDialog', true)) {
      this.router.navigate(['/recipes/edit/' + recipe.id]);
      return;
    }
    this.recipeEditDialog = this.dialogService.open(DialogRecipeFormComponent, {
      header: '', // this.translate.instant('EDITRecipe') + ' ' + Recipe.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        recipe: recipe,
 
        users: this.recipesColumnsService.users,
      },
    });

    this.recipeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: this.translate.instant('recipeUPDATED') });
        this.getRecipes();
      }
    });
  }

  recipeDetails(recipe: Recipe): void {
    if (!this.navigation.getParamValue('recipes.detailsInDialog', true)) {
      this.router.navigate(['/recipes/details/' + recipe.id]);
      return;
    }
    this.recipeDetailsDialog = this.dialogService.open(DialogRecipeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        recipe: recipe,
      }
    });
  }

  recipeDelete(recipe: Recipe): void {
    this.recipeToDelete = recipe;
    this.messageService.clear();
    this.messageService.add({
      key: 'recipeDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + recipe.title + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.recipesDataService.deleteRecipe(this.recipeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: this.translate.instant('recipeDELETED') });
      this.getRecipes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('recipeNOTDELETED') });
    });
  }

  onDeleteReject(): void {
    this.recipeToDelete = {};
    this.messageService.clear('postDelete');
  }

}
